export const MEGABYTE = 1048576;
export const MAX_FILE_SIZE = 10485760; //10MB
export const MAX_FILE_SIZE_25 = 25 * MEGABYTE;

export type FileType =
  | 'application/pdf'
  | 'application/vnd.ms-excel'
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'image/png'
  | 'image/jpeg'
  | 'image/gif'
  | 'application/zip'
  | 'application/x-7z-compressed'
  | 'text/plain'
  | 'text/csv'
  | 'application/vnd.ms-excel'; // csv for Firefox

interface IFileSignature {
  type: FileType;
  extension?: string[];
  sign: string;
}

//https://en.wikipedia.org/wiki/List_of_file_signatures
const signatures: IFileSignature[] = [
  { type: 'application/pdf', extension: ['pdf'], sign: ['25', '50', '44', '46', '2D'].join('') },
  {
    type: 'application/vnd.ms-excel',
    extension: ['xls'],
    sign: ['D0', 'CF', '11', 'E0', 'A1', 'B1', '1A', 'E1'].join(''),
  }, //eg. xls
  {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: ['xlsx'],
    sign: ['50', '4B', '03', '04'].join(''),
  }, //eg. xlsx
  { type: 'image/png', extension: ['png'], sign: ['89', '50', '4E', '47', '0D', '0A', '1A', '0A'].join('') },
  { type: 'image/jpeg', extension: ['jpg'], sign: ['FF', 'D8', 'FF', 'DB'].join('') },
  { type: 'image/jpeg', extension: ['jpeg'], sign: ['FF', 'D8', 'FF', 'DB'].join('') },
  {
    type: 'image/jpeg',
    extension: ['jpg'],
    sign: ['FF', 'D8', 'FF', 'E0', '00', '10', '4A', '46', '49', '46', '00', '01'].join(''),
  },
  { type: 'image/jpeg', extension: ['jpg'], sign: ['FF', 'D8', 'FF', 'EE'].join('') },
  { type: 'image/jpeg', extension: ['jpg'], sign: ['FF', 'D8', 'FF', 'E1'].join('') },
  { type: 'image/jpeg', extension: ['jpg'], sign: ['FF', 'D8', 'FF', 'E0'].join('') },
  { type: 'image/gif', extension: ['gif'], sign: ['47', '49', '46', '38', '37', '61'].join('') }, //GIF87a
  { type: 'image/gif', extension: ['gif'], sign: ['47', '49', '46', '38', '39', '61'].join('') }, //GIF89a
  { type: 'application/zip', extension: ['zip'], sign: ['1F', '8B'].join('') },
  { type: 'application/x-7z-compressed', extension: ['7z'], sign: ['37', '7A', 'BC', 'AF', '27', '1C'].join('') },
  { type: 'text/plain', extension: ['txt'], sign: ['EF', 'BB', 'BF'].join('') }, //UTF8
  { type: 'text/plain', sign: ['FF', 'FE'].join('') }, //UTF16LE
  { type: 'text/plain', sign: ['FE', 'FF'].join('') }, //UTF16BE
  { type: 'text/plain', sign: ['FF', 'FE', '00', '00'].join('') }, //UTF32LE
  { type: 'text/plain', sign: ['00', '00', 'FE', 'FF'].join('') }, //UTF32BE
  { type: 'text/plain', sign: ['0E', 'FE', 'FF'].join('') }, //SCSU
];

export const getFileTypeFromExtension = (extension: string): FileType | null => {
  const signature = signatures.find((item) => item.extension && item.extension.includes(extension));
  if (signature) {
    return signature.type;
  } else {
    return null;
  }
};

export function extractFileType(buffer: ArrayBuffer): FileType | null {
  const hex = new Array(8);
  new Uint8Array(buffer.slice(0, 8)).forEach((byte, index) => {
    const hexValue = byte.toString(16).padStart(2, '0').toUpperCase();
    if (hexValue) {
      hex[index] = hexValue;
    }
  });

  const foundSignature = signatures.find((signature: IFileSignature) => {
    if (hex.slice(0, signature.sign.length / 2).join('') === signature.sign) {
      return true;
    }
  });

  return foundSignature ? foundSignature.type : null;
}

export interface IFileTypeExtractingResult {
  acceptedFileType: boolean;
  fileExtension: string | undefined;
}

export function extractFileTypeForTask(file: string, array: string[]): IFileTypeExtractingResult {
  const extension = file.split('.').pop();
  return { acceptedFileType: array.some((e) => e.toLowerCase() === extension), fileExtension: extension };
}

export const getMaxFileSizeInBytes = (numberOfMegaBytes: number) => numberOfMegaBytes * MEGABYTE;

export const formatBytes = (bytes: number, decimals = 0) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export type ExportType =
  | 'ZWO'
  | 'uzytkownicy'
  | 'wykaz_zdajacych'
  | 'wykaz_egzaminatorow'
  | 'karty_rozwiazan'
  | 'wyniki_egzaminu'
  | 'RozliczenieRGZ';

export const getExtFromFileName = (fileName: string): string => {
  if (!fileName || fileName.length === 0) return '';
  const index = fileName.lastIndexOf('.');
  if (index >= 0 && index + 1 < fileName.length) return fileName.substring(index + 1);
  return '';
};
